import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency amount based on currency code
 * - LAK: Lao number format + "ກີບ" (e.g. "500,000 ກີບ", "50 ລ້ານ ກີບ")
 * - USD: Dollar sign + US format (e.g. "$50,000.00")
 * - THB: Baht sign + Thai format (e.g. "฿50,000.00")
 *
 * Backward compatible: callers that don't pass currency still get LAK format.
 *
 * @param amount - The amount to format (number, string, or null)
 * @param currency - Currency code: "LAK" | "USD" | "THB" (default: "LAK")
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(50000, "LAK")  // "50,000 ກີບ"
 * formatCurrency(50000, "USD")  // "$50,000.00"
 * formatCurrency(50000, "THB")  // "฿50,000.00"
 * formatCurrency(50000)         // "50,000 ກີບ" (backward compat)
 */
export function formatCurrency(amount: number | string | null | undefined, currency: string = "LAK"): string {
  if (amount === null || amount === undefined || amount === "") return "-";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "-";

  if (currency === "USD") {
    return `$${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)}`;
  }

  if (currency === "THB") {
    return `฿${new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)}`;
  }

  // LAK default — use Lao abbreviated format for large numbers
  return `${formatLaoNumber(num)} ກີບ`;
}

/**
 * Format large numbers to Lao format (ລ້ານ, ຕື້)
 * - Numbers >= 1,000,000,000 → "X ຕື້"
 * - Numbers >= 1,000,000 → "X ລ້ານ"
 * - Numbers < 1,000,000 → unchanged with thousand separators
 * 
 * @param num - The number to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string
 * 
 * @example
 * formatLaoNumber(5000000000) // "5 ຕື້"
 * formatLaoNumber(50000000) // "50 ລ້ານ"
 * formatLaoNumber(5500000) // "5.5 ລ້ານ"
 * formatLaoNumber(500000) // "500,000"
 */
export function formatLaoNumber(num: number, decimals: number = 1): string {
  if (num >= 1000000000) {
    // ຕື້ (billion)
    const value = num / 1000000000;
    return value % 1 === 0 
      ? `${value.toFixed(0)} ຕື້`
      : `${value.toFixed(decimals)} ຕື້`;
  } else if (num >= 1000000) {
    // ລ້ານ (million)
    const value = num / 1000000;
    return value % 1 === 0
      ? `${value.toFixed(0)} ລ້ານ`
      : `${value.toFixed(decimals)} ລ້ານ`;
  } else {
    // Format with thousand separators
    return new Intl.NumberFormat("lo-LA").format(num);
  }
}

/**
 * Format currency with Lao number format (backward-compatible wrapper)
 * Delegates to formatCurrency — supports LAK, USD, THB.
 * 
 * @param amount - The amount to format
 * @param currency - Currency code (default: "LAK")
 * @returns Formatted currency string
 * 
 * @example
 * formatLaoCurrency(50000000)          // "50 ລ້ານ ກີບ"
 * formatLaoCurrency(500000)            // "500,000 ກີບ"
 * formatLaoCurrency(50000, "USD")      // "$50,000.00"
 * formatLaoCurrency(50000, "THB")      // "฿50,000.00"
 */
export function formatLaoCurrency(amount: number | string | null | undefined, currency: string = "LAK"): string {
  return formatCurrency(amount, currency);
}
